/**
 * Apartment card used in the Property detail "Apartments" tab grid.
 *
 * One card per owner-owned unit. Shows a status accent strip (fully /
 * partially occupied / vacant), the apartment label + floor/size, the
 * owner (lessor) name + avatar, room occupancy progress, monthly room
 * revenue and an estimated gross yield. The whole card navigates to the
 * apartment detail page. Read-only (COL-225 phase 1).
 */
import { useNavigate } from "react-router-dom";
import { AlertTriangle, TrendingUp, User } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { formatEUR, initialsFromName } from "@/lib/v2-format";
import type { ApartmentListItem } from "@/types/property";
import { useLanguage } from "@/contexts/LanguageContext";
import { unitMonthlyRevenue } from "@/lib/owner-metrics";

type Props = {
  apartment: ApartmentListItem;
  /** Resolved owner (lessor) display name, if linked. */
  ownerName: string | null;
  /**
   * Rent actually collected this calendar month, across the apartment's
   * rooms. Computed once for the whole property grid (see ApartmentsTab)
   * rather than per card, so an N-apartment property issues one set of
   * month-rent queries instead of N. `null` while that shared query is
   * still loading — rendered as "—", never as a misleading €0.00.
   */
  currentMonthRevenue: number | null;
  /**
   * No representor resolves for this apartment, so its contracts would print
   * no "vertreten durch" (COL-466). Resolved once for the whole grid — see
   * ApartmentsTab's usePropertyRepresentorGaps.
   */
  missingRepresentor?: boolean;
};

/** full = all rooms occupied · part = some occupied · vacant = none. */
function deriveStatus(a: ApartmentListItem): "full" | "part" | "vacant" {
  if (a.rooms_count === 0 || a.occupied_count === 0) return "vacant";
  if (a.occupied_count >= a.rooms_count) return "full";
  return "part";
}

export function ApartmentCard({
  apartment: a,
  ownerName,
  currentMonthRevenue,
  missingRepresentor = false,
}: Props) {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const occPct =
    a.rooms_count > 0
      ? Math.round((a.occupied_count / a.rooms_count) * 100)
      : 0;
  const status = deriveStatus(a);
  const statusLabel =
    status === "full"
      ? t("admin.apartments.fullyOccupied", "Fully occupied")
      : status === "part"
        ? t("admin.apartments.partiallyOccupied", "Partially occupied")
        : t("admin.apartments.vacant", "Vacant");
  const accent =
    status === "full"
      ? "bg-teal-500"
      : status === "part"
        ? "bg-amber-500"
        : "bg-rose-500";

  // Estimated gross yield = annual CoLiviq margin / invested capital.
  //
  // Through unitMonthlyRevenue, not a local `rent − master_rent`: that
  // reimplementation ignored `revenue_model` (a pct_of_rent or
  // flat_fee_per_room apartment got the full_rent formula too) and predated
  // the rule that a full_rent unit with no master rent on file is billed
  // NOTHING rather than its whole rent roll (20260908140000). Both readings
  // fed a yield figure the invoice would never support. Room revenue and room
  // COUNT are the basis, so this stays the estimate at current pricing rather
  // than becoming a reading of who is occupied this month.
  const margin = unitMonthlyRevenue(
    {
      revenue_model: a.revenue_model,
      coliviq_fee_pct: a.coliviq_fee_pct,
      coliviq_fee_amount: a.coliviq_fee_amount,
      master_rent: a.master_rent,
    },
    a.rooms_count,
    a.rooms_revenue
  );
  const yieldPct =
    a.invested_amount && a.invested_amount > 0
      ? ((margin * 12) / a.invested_amount) * 100
      : null;

  const name = ownerName ?? t("admin.apartments.unassigned", "Unassigned");

  const monthLabel = new Date().toLocaleDateString(
    language === "de" ? "de-DE" : "en-GB",
    { month: "long" }
  );

  return (
    <div
      role="link"
      tabIndex={0}
      onClick={() =>
        navigate(`/admin/properties/${a.property_id}/apartments/${a.id}`)
      }
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ")
          navigate(`/admin/properties/${a.property_id}/apartments/${a.id}`);
      }}
      className="group block focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-xl cursor-pointer"
    >
      <Card className="overflow-hidden transition-all duration-200 group-hover:-translate-y-0.5 group-hover:shadow-lg">
        <div className={`h-1 ${accent}`} />
        <div className="p-4 space-y-3.5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="font-bold tracking-tight truncate">
                  {a.label}
                </span>
                <Badge
                  variant={status === "full" ? "default" : "outline"}
                  className="text-[10px] shrink-0"
                >
                  {statusLabel}
                </Badge>
                {missingRepresentor && (
                  <Badge
                    variant="outline"
                    className="shrink-0 gap-1 border-amber-200 bg-amber-50 font-normal text-[10px] text-amber-800"
                    title={t(
                      "admin.apartments.noRepresentorBadgeHint",
                      "Contracts for this apartment would have no “vertreten durch”. Set a representor on the apartment, its owner or the property."
                    )}
                  >
                    <AlertTriangle className="h-3 w-3" aria-hidden />
                    {t("admin.apartments.noRepresentorBadge", "No representor")}
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                {a.floor
                  ? `${t("admin.apartments.floor", "Floor")} ${a.floor}`
                  : `${t("admin.apartments.floor", "Floor")} —`}
                {a.size_sqm != null && ` · ${a.size_sqm} m²`}
              </p>
            </div>
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-teal-100 text-xs font-bold text-teal-700">
              {initialsFromName(name)}
            </div>
          </div>

          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <User className="h-3 w-3 shrink-0" />
            <span className="truncate">{name}</span>
          </p>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">
                {a.occupied_count} / {a.rooms_count}{" "}
                {t("admin.apartments.roomsOccupied", "Rooms occupied")}
              </span>
              <span className="font-semibold tabular-nums">{occPct}%</span>
            </div>
            <Progress value={occPct} />
          </div>

          <div className="flex items-center justify-between gap-3 border-t pt-3">
            <div>
              <p className="text-[11px] text-muted-foreground">
                {t("admin.apartments.expectedRevenue", "Expected revenue")}
              </p>
              <p className="text-lg font-bold tracking-tight tabular-nums">
                {formatEUR(a.rooms_revenue) ?? "—"}
              </p>
            </div>
            <div>
              <p className="text-[11px] text-muted-foreground">
                {monthLabel} {t("admin.apartments.revenue", "Revenue")}
              </p>
              <p className="text-lg font-bold tracking-tight tabular-nums">
                {currentMonthRevenue != null
                  ? (formatEUR(currentMonthRevenue) ?? "—")
                  : "—"}
              </p>
            </div>
            <div className="text-right shrink-0">
              <p className="text-[11px] text-muted-foreground">
                {t("admin.apartments.yield", "Yield")}
              </p>
              <p className="flex items-center justify-end gap-1 text-sm font-bold text-teal-600 tabular-nums">
                <TrendingUp className="h-3.5 w-3.5" />
                {yieldPct != null ? `${yieldPct.toFixed(1)}%` : "—"}
              </p>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
